<template>
  <div
    v-if="cartItem.additionalInfo.isTempRemoved"
    id="cart-item-removed"
    class="cart-item--removed"
    :class="{
      'cart-item--error': !isAvailable,
      'cart-item--highlight': showHighlight
    }"
  >
    <div>
      <BaseButton
        theme="link"
        class="cart-item__name-wrap"
        @click="
          handleOpenProduct(
            localePath(
              `/product/${cartItem.product.slug}/${cartItem.product.uuid}/${cartItem.code}`
            ),
            cartItem.code
          )
        "
      >
        <span class="cart-item__name">
          <span class="cart-item__name-status">{{ conditionStatus }}&nbsp;</span>
          <span class="cart-item__name-value">{{ cartItem.product.name }}</span>
        </span>
      </BaseButton>
      {{ $t('cart.delete_success') }}
    </div>

    <BaseButton
      class="cart-item__button-restore"
      theme="link"
      @click="handleRestoreOffer"
    >
      {{ $t('cart.restore') }}
    </BaseButton>
  </div>

  <div
    v-else
    id="cart-item"
    class="cart-item"
    :class="{
      'cart-item--error': !isAvailable,
      'cart-item--highlight': showHighlight
    }"
  >
    <div class="cart-item__about">
      <figure
        class="cart-item__image"
        @click="() => handleClickImage(cartItem.code)"
      >
        <BaseImage
          class="cart-item__image-img"
          size="100x100"
          :name="cartItem.images[0]"
          :alt="cartItem.product.name"
        />
      </figure>

      <div class="cart-item__offer">
        <p class="cart-item__code">
          {{ $t('offers.code') }} {{ cartItem.code }}
        </p>

        <BaseButton
          theme="link"
          class="cart-item__name-wrap z-index__4"
          :class="{ 'cart-item__name-wrap--disabled': !isAvailable }"
          @click="
            handleOpenProduct(
              localePath(
                `/product/${cartItem.product.slug}/${cartItem.product.uuid}/${cartItem.code}`
              ),
              cartItem.code
            )
          "
        >
          <span class="cart-item__name">
            <span class="cart-item__name-status">{{ conditionStatus }}&nbsp;</span>
            <span class="cart-item__name-value">{{ cartItem.product.name }}</span>
          </span>
        </BaseButton>

        <!-- INCORRECT QUANTITY -->
        <div v-if="!isAvailable" class="cart-item__not-in-stock z-index__5">
          {{ $t('cart.offer_not_available') }}
        </div>
      </div>
    </div>
    <!-- QUANTITY OFFER -->
    <div id="cart-item-qty" class="cart-item__qty">
      <BaseCount
        v-model="count"
        :min="1"
        :max="cartItem.quantity_max"
        :disabled="updating"
        use-correction
        @input-max-error="isErrorInputMax = $event"
      />
      <div
        v-if="isErrorInputMax || hasErrorQty"
        class="cart-item__not-in-stock z-index__4"
      >
        {{ $t('cart.order_lack_qty_notify', cartItem.quantity_max) }}
      </div>
    </div>
    <!---->
    <!-- PRICE OFFER -->
    <div id="cart-price" class="cart-item__price">
      <CartItemPrice
        :price="cartItem.price_unit"
        :price-old="cartItem.additionalInfo.priceOld"
      />
    </div>
    <!---->
    <!-- TOTAL PRICE OFFER -->
    <div id="cart-price-total" class="cart-item__price cart-item__price-total">
      <CartItemPriceTotal
        :update="isUserInputUpdate"
        :price-total="cartItemTotal"
      />
    </div>
    <!---->
    <!-- REMOVE OFFER -->
    <div class="cart-item__action z-index__5">
      <span class="cart-item__remove icon-delete" @click="handleRemoveOffer" />
    </div>
    <!---->
    <div v-if="!isAvailable" class="loading-overlay" />
  </div>
</template>

<script lang="ts" setup>
import { CONDITION_NEW } from '@/constants/offers'
import type { CartItem } from '~/types/checkout'

type Props = {
  orderId: string
  cartItem: CartItem
  updating: boolean
  showHighlight?: null | ReturnType<typeof setTimeout>
}
type Emits = {
  (eventName: 'input-is-updating', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  showHighlight: null
})
const emit = defineEmits<Emits>()
const { delayId, handleDelay } = useDelay()
const router = useRouter()
const commonMetricsStore = useCommonMetricsStore()
const cartStore = useCartStore()
const localePath = useLocalePath()

// достигнуто максимальне вхідне значення
const isErrorInputMax = ref<boolean>(false)

// Computed
// пропозиція недоступна
const isAvailable = computed<boolean>(() => {
  return props.cartItem.quantity_max > 0 && props.cartItem.additionalInfo.isAvailable
})

// Потрібно обрахувати фінальну ціну так як price_unit оновлюється ендпоінтом is-available
const cartItemTotal = computed(() => {
  return props.cartItem.price_unit * props.cartItem.quantity
})

// чи проводиться на даний момент процес оновлення кількості пропозиції
const isUserInputUpdate = computed<boolean>(() => {
  return delayId.value !== null || props.updating
})

// чи є пропозиції з помилкою кількості
const hasErrorQty = computed<boolean>(() => {
  return count.value > props.cartItem.quantity_max
})

const count = computed({
  get: () => {
    return props.cartItem.quantity
  },

  // встановлення нової кількості пропозицій
  set: (value: number) => {
    // delay handler requests to change the quantity of offers
    // create new delay quantity of offers
    emit('input-is-updating', true)

    handleDelay(() => {
      // update quantity of offer
      handleUpdate(value)
    }, 250)

    commonMetricsStore.addMetricEvent({
      element: 'ms[cart-product-quantity]',
      event_type: 'click',
      event_content: {
        cs_code: props.cartItem.code,
        count: value
      }
    })
  }
})

const conditionStatus = computed<string>(() => {
  return getConditionNameShort(
    {
      condition: props.cartItem.condition,
      ignored: [CONDITION_NEW],
      value: 2
    }
  )
})

// Methods
// Оновити кількість пропозицій
const handleUpdate = (value: number) => {
  cartStore.updateCartOffer({
    orderId: props.orderId,
    offerId: props.cartItem.uuid,
    quantity: value
  })

  // Користувач закінчив змінювати кількість в полі вводу
  emit('input-is-updating', false)
}

const handleRemoveOffer = () => {
  cartStore.tempRemoveCartOffer({
    orderId: props.orderId,
    offerId: props.cartItem.uuid
  })

  commonMetricsStore.addMetricEvent({
    element: 'ms[cart-product-remove]',
    event_type: 'click',
    event_content: props.cartItem.code
  })
}

const handleRestoreOffer = () => {
  cartStore.restoreCartOffer({
    orderId: props.orderId,
    offerId: props.cartItem.uuid
  })
}

const handleOpenProduct = (url: string, offerCode: string) => {
  commonMetricsStore.addMetricEvent({
    element: 'ms[cart-product-link]',
    event_type: 'click',
    event_content: offerCode
  })

  router.push(url)
}

const handleClickImage = (offerCode: string) => {
  commonMetricsStore.addMetricEvent({
    element: 'ms[cart-product-image]',
    event_type: 'click',
    event_content: offerCode
  })
}
</script>

<style lang="scss" scoped>
@keyframes highlight {
  0% {
    background-color: #888888;
  }
  100% {
    background-color: transparent;
  }
}

.cart-item {
  $root: &;
  position: relative;
  min-height: 5.8rem;

  &__image {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    width: 4rem;
    height: 4rem;
    margin-right: 1.2rem;

    &-img {
      max-width: 100%;
      max-height: 100%;
    }
  }

  &__button-restore {
    margin-left: 1.6rem;
  }

  &--removed {
    position: relative;
    min-height: initial;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    color: $secondary-font-color;
    #{$root}__name-wrap {
      display: inline;
    }
    @media only screen and (min-width: 1024px) {
      #{$root}__name {
        max-width: none;
      }
    }
  }

  &--error {
    .loading-overlay {
      top: -0.8rem;
      bottom: -0.8rem;
      left: -1.6rem;
      right: -1.6rem;
    }
  }

  &--highlight {
    &:before {
      opacity: 0.08;
      content: '';
      position: absolute;
      top: -0.8rem;
      bottom: -0.8rem;
      left: -1.6rem;
      right: -1.6rem;
      display: block;
      animation: highlight 1.5s ease-out;
      z-index: 2;
      pointer-events: none;
    }
  }

  @media only screen and (max-width: 740px) {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  @media only screen and (min-width: 740px) {
    display: grid;
    grid-gap: 1rem 2.5rem;
    grid-template-columns: 2fr 2fr 13rem 15rem;
    min-height: 6rem;
  }

  @media only screen and (min-width: 1024px) {
    grid-template-columns: 5fr 10rem 9.2rem 17rem max-content;
    align-items: center;
    grid-gap: 1rem 4.9rem;
  }

  &__about {
    display: flex;

    @media only screen and (max-width: 740px) {
      margin-bottom: 1rem;
      width: 92%;
      order: 1;
    }
    @media only screen and (min-width: 740px) and (max-width: 1024px) {
      grid-column: 1/4;
    }
  }

  &__code {
    font-size: $font-size-small_syn;
    line-height: 118%;
    color: $secondary-font-color;
    margin-bottom: 0.4rem;
  }

  &__not-in-stock {
    margin-top: 4px;
    line-height: $line-height-16;
    color: $error-color;
    position: relative;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__offer {
    position: relative;
  }

  &__name {
    word-wrap: break-word;
    display: inline-block;

    @media only screen and (max-width: 740px) {
      max-width: calc(100vw - 11rem);
    }

    @media only screen and (min-width: 1024px) {
      max-width: 31rem;
    }

    &-wrap {
      text-align: left;
      line-height: 2rem;
      position: relative;
      &--disabled {
        span {
          color: $font-color;
          opacity: 0.5;
        }
      }
    }

    &-status {
      color: $font-color;
    }

    &-value {
      white-space: pre-wrap;
      text-align: left;
    }
  }

  &__price {
    position: relative;
    min-width: 7rem;

    &--update {
      opacity: 0.4;
    }

    @media only screen and (min-width: 740px) {
      min-width: 12rem;
    }

    @media only screen and (min-width: 740px) and (max-width: 1024px) {
      &-total {
        grid-column: 4/-1;
      }
    }

    @media only screen and (max-width: 740px) {
      width: min-content;
    }
    @media only screen and (max-width: 1024px) {
      order: 4;
      &-total {
        display: flex;
        justify-content: flex-end;
        min-width: 8rem;
      }
    }
    @media only screen and (min-width: 1024px) {
      padding-top: 0.4rem;
    }
  }

  &__qty {
    position: relative;
    @media only screen and (max-width: 740px) {
      width: max-content;
      height: max-content;
      order: 3;
    }
    @media only screen and (min-width: 1024px) {
      #{$root}__not-in-stock {
        position: absolute;
      }
    }
    @media only screen and (min-width: 740px) and (max-width: 1024px) {
      order: 3;
      grid-column: 2/3;
    }
  }

  &__action {
    position: relative;
    @media only screen and (max-width: 740px) {
      display: flex;
      align-self: flex-start;
      width: max-content;
      order: 2;
    }
    @media only screen and (min-width: 740px) and (max-width: 1024px) {
      justify-self: flex-end;
      order: 2;
    }
  }

  &__remove {
    display: inline-block;
    cursor: pointer;
    color: #bababa;
    font-size: 1.9rem;

    &:hover {
      opacity: 0.7;
    }

    &.icon-spinner-third {
      animation: rotate 0.8s infinite;
    }
  }
}
</style>
